import { t } from '../../utils/i18n';
import { SCREEN } from '../../utils/constants';
import {
    appUrl,
    appointmentDuration,
    appointmentSummary,
    eventId,
    form,
    locations,
    primaryColor,
    providers,
    setEventPayload,
    setEventRoom,
} from '../../utils/variables';
import { procedureDisplayName } from '../../utils/procedureDisplay';
import { isVirtualVisit, remoteVisitLabelKey } from '../../utils/deliveryMode';
import {
    updateEvevntStatus,
    saveEventPaymentMethod,
    createEventPaymentMethodSetupIntent,
} from '../../services/selectTimeService';
import { loadAppointmentConfirmed } from '../../views/components/appointmentConfirmed';
import { appointmentConfirmedListeners } from './appointmentConfirmedListeners';
import { navigate, triggerCalendarRedirectIfConfigured } from './movePages';
import { toastError } from '../../views/components/error';

let stripeInstance = null;
let elementsInstance = null;
let cardElement = null;
let walletElements = null;
let expressCheckoutElement = null;
let _cardSubmitListenerAttached = false;

function loadStripeScript() {
    return new Promise((resolve) => {
        if (window.Stripe) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => resolve(); // still resolve so we can show error in UI
        document.head.appendChild(script);
    });
}

function mountCardElement(publishableKey, stripeAccountId = null) {
    if (!window.Stripe || !publishableKey) return;
    stripeInstance = stripeAccountId
        ? window.Stripe(publishableKey, { stripeAccount: stripeAccountId })
        : window.Stripe(publishableKey);
    const elements = stripeInstance.elements();
    const style = {
        base: {
            fontSize: '16px',
            color: '#1b2e4b',
            '::placeholder': { color: '#aab7c4' },
        },
        invalid: {
            color: '#fa755a',
        },
    };
    cardElement = elements.create('card', { style });
    const container = document.getElementById('card-element');
    if (container && container.children.length === 0) {
        cardElement.mount('#card-element');
    }
    elementsInstance = elements;

    mountExpressCheckoutElement();
}

/**
 * Apple Pay / Google Pay, via the Express Checkout Element.
 *
 * Deliberately mounted on its OWN Elements group (deferred `mode: 'setup'`) rather
 * than the group holding the Card Element: confirmSetup({ elements }) confirms the
 * whole group, so sharing one group would entangle the wallet and typed-card paths.
 * This way the existing card flow is untouched — if wallets are unavailable, Stripe
 * simply reports no available payment methods and we leave the block hidden.
 *
 * The SetupIntent the backend creates already uses payment_method_types: ['card'],
 * which covers both wallets — they tokenize into ordinary card PaymentMethods, so
 * nothing changes server-side or in how the saved card is later charged.
 */
function mountExpressCheckoutElement() {
    const container = document.getElementById('express-checkout-element');
    if (!container || container.children.length > 0) return;

    let elements;
    try {
        elements = stripeInstance.elements({
            mode: 'setup',
            currency: 'usd',
            paymentMethodTypes: ['card'],
            setupFutureUsage: 'off_session',
        });
    } catch (_) {
        // Older stripe.js without deferred-intent support: card form still works.
        return;
    }

    if (typeof elements.create !== 'function') return;

    try {
        expressCheckoutElement = elements.create('expressCheckout', {
            paymentMethods: { applePay: 'auto', googlePay: 'auto' },
            buttonHeight: 44,
        });
    } catch (_) {
        return;
    }

    walletElements = elements;

    expressCheckoutElement.on('ready', (event) => {
        const available = event?.availablePaymentMethods;
        const hasWallet = available && Object.values(available).some(Boolean);
        if (hasWallet) {
            document.querySelector('.card-collection__wallets')?.classList.remove('hidden');
        }
    });

    // A wallet failing must never block the typed-card path, so surface the error
    // in the shared error slot and leave the card form usable.
    expressCheckoutElement.on('loaderror', () => {
        document.querySelector('.card-collection__wallets')?.classList.add('hidden');
    });

    expressCheckoutElement.on('confirm', async () => {
        showCardError('');
        try {
            const clientSecret = await createEventPaymentMethodSetupIntent(eventId);
            if (!clientSecret) {
                showCardError('Could not start card setup. Please try again.');
                return;
            }

            const { setupIntent, error } = await stripeInstance.confirmSetup({
                elements: walletElements,
                clientSecret,
                // Wallets never redirect, so we get the SetupIntent back inline and
                // can reuse the exact same save + confirm tail as the card path.
                redirect: 'if_required',
            });

            if (error) {
                showCardError(error.message || 'Payment could not be completed.');
                return;
            }

            const paymentMethodId = setupIntent?.payment_method ?? setupIntent?.paymentMethod;
            if (!paymentMethodId) {
                showCardError('Could not save payment method. Please try again.');
                return;
            }

            await saveEventPaymentMethod(eventId, typeof paymentMethodId === 'string' ? paymentMethodId : paymentMethodId.id);
            await confirmAfterCardSaved();
        } catch (err) {
            showCardError(err.response?.data?.message || 'Could not complete payment. Please try again.');
        }
    });

    expressCheckoutElement.mount('#express-checkout-element');
}

function showCardError(message) {
    const el = document.getElementById('card-errors');
    if (el) {
        el.textContent = message || '';
        el.style.display = message ? 'block' : 'none';
    }
}

async function confirmAfterCardSaved() {
    const btn = document.querySelector('.card-collection__continue-btn');
    const loadingWrapper = document.querySelector('.card-collection__continue-btn .loading__wrapper');
    try {
        btn?.classList.add('disabled');
        if (loadingWrapper) {
            loadingWrapper.classList.remove('hidden');
            loadingWrapper.innerHTML = '';
            loadingWrapper.insertAdjacentHTML('beforeend', `<img class="loader-spinner" src="${appUrl}/assets/icons/rolling.png" alt="" />`);
        }

        const updateRes = await updateEvevntStatus(eventId);

        // Slot → intake handoff (see selectTimeActions.js) — card path included,
        // so a deposit widget with an intake configured hands off the same way.
        const intakeUrl = updateRes?.data?.intake_redirect_url;
        if (intakeUrl) {
            window.parent.location.href = intakeUrl;
            return;
        }

        setEventRoom(updateRes.data.event_room || null);
        setEventPayload(updateRes.data);

        // Always show appointment thank you / event summary (confirmed or request)
        loadAppointmentConfirmed();
        appointmentConfirmedListeners();
        navigate(SCREEN.APPOINTMENT_CONFIRMED);
        triggerCalendarRedirectIfConfigured();
    } catch (err) {
        toastError(err.response?.data?.message || 'Something went wrong');
    } finally {
        document.querySelector('.card-collection__continue-btn .loading__wrapper')?.classList.add('hidden');
        document.querySelector('.card-collection__continue-btn')?.classList.remove('disabled');
    }
}

/**
 * Compact appointment recap shown above the card form. The card screen is now
 * the step right after slot selection (no separate summary screen in the
 * booking flow), so the patient needs to see what they're confirming here.
 */
function renderCardCollectionRecap() {
    const recapEl = document.querySelector('.card-collection__recap');
    if (!recapEl) return;

    const event = appointmentSummary?.event;
    if (!event?.start_time) {
        recapEl.style.display = 'none';
        return;
    }

    const startTime = moment(event.start_time * 1000);

    const staffIds = appointmentSummary.staffs?.map(i => i.participable_id) ?? [];
    const staffNames = providers
        .filter(p => staffIds.includes(p.id))
        .map(p => `${p.first_name} ${p.last_name}`)
        .join(', ');

    const selectedProcedures = event.procedures ?? [];
    const isRemote = isVirtualVisit(form, selectedProcedures);
    const locationObj = locations.find(l => l.id === event.location_id);
    // Location name only — the street address belongs on the confirmation
    // screen, not the payment step.
    const locationLine = isRemote
        ? t(remoteVisitLabelKey(selectedProcedures))
        : (locationObj?.name || '');

    const serviceNames = selectedProcedures
        .map(p => procedureDisplayName(p))
        .filter(Boolean)
        .join(', ');

    const durationMinutes = appointmentDuration?.duration;
    let durationLabel = '';
    if (durationMinutes) {
        const dur = moment.duration(durationMinutes, 'm');
        durationLabel = dur.minutes() > 0
            ? `${dur.format('h[h] m')}min`
            : dur.format('h[h]');
    }

    const detailRow = (icon, text) => `
        <div class="recap__row">
            <i class="${icon}"></i>
            <span class="recap__detail">${text}</span>
        </div>`;

    recapEl.style.display = '';
    recapEl.innerHTML = `
        <div class="recap__row recap__row--lead">
            <i class="ri-time-line"></i>
            <div class="recap__col">
                <div class="recap__primary">
                    <span class="recap__time">${startTime.format('hh:mm A')}</span>
                    ${durationLabel ? `<span class="recap__duration">${durationLabel}</span>` : ''}
                </div>
                <div class="recap__detail">${startTime.format('dddd, MMMM D, YYYY')}</div>
            </div>
        </div>
        ${staffNames ? detailRow('ri-user-3-line', staffNames) : ''}
        ${locationLine ? detailRow('ri-map-pin-2-line', locationLine) : ''}
        ${serviceNames ? detailRow('ri-stethoscope-line', serviceNames) : ''}
    `;
    const durationEl = recapEl.querySelector('.recap__duration');
    if (durationEl) {
        durationEl.style.background = `${primaryColor}14`;
        durationEl.style.color = primaryColor;
    }
}

/**
 * Single entry point for the card-collection step: render the appointment
 * recap, show the screen, and wire the Stripe form.
 */
export function loadCardCollection() {
    renderCardCollectionRecap();
    navigate(SCREEN.CARD_COLLECTION);
    cardCollectionListeners();
}

function setCardIntroMessage() {
    const introEl = document.querySelector('.card-collection__intro');
    if (!introEl) return;
    const custom = form?.appointment_channel?.card_collection_message?.trim();
    if (custom) {
        introEl.textContent = custom;
        return;
    }
    const mode = form?.appointment_channel?.card_collection_mode ?? 'on_file';
    if (mode === 'deposit') {
        const cents = form?.appointment_channel?.deposit_amount_cents;
        const amount = cents != null && cents >= 50 ? (cents / 100).toFixed(2) : null;
        introEl.textContent = amount != null
            ? `A deposit of $${amount} will be charged to your card to confirm your appointment.`
            : 'A deposit will be charged to your card to confirm your appointment.';
    } else {
        introEl.textContent = "Your card will be saved securely on file. It may be charged according to the practice's cancellation policy.";
    }
}

export function cardCollectionListeners() {
    const publishableKey = form?.stripe_publishable_key;
    if (!publishableKey) {
        showCardError('Payment is not configured for this practice.');
        return;
    }

    setCardIntroMessage();

    loadStripeScript().then(() => {
        requestAnimationFrame(() => mountCardElement(publishableKey, form?.stripe_account ?? null));
    });

    const errorsEl = document.getElementById('card-errors');
    if (errorsEl) errorsEl.style.display = 'none';

    const submitBtn = document.querySelector('.card-collection__continue-btn');
    if (!submitBtn) return;

    if (_cardSubmitListenerAttached) return;
    _cardSubmitListenerAttached = true;

    submitBtn.addEventListener('click', async () => {
        if (!stripeInstance || !cardElement || !eventId) {
            showCardError('Please wait for the form to load.');
            return;
        }

        showCardError('');

        const loadingWrapper = submitBtn.querySelector('.loading__wrapper');
        try {
            submitBtn.classList.add('disabled');
            if (loadingWrapper) {
                loadingWrapper.classList.remove('hidden');
                loadingWrapper.innerHTML = '';
                loadingWrapper.insertAdjacentHTML('beforeend', `<img class="loader-spinner" src="${appUrl}/assets/icons/rolling.png" alt="" />`);
            }

            // Use SetupIntent so the PaymentMethod is created in the correct Stripe account
            // (platform or Connect) without needing the connected account's publishable key.
            const clientSecret = await createEventPaymentMethodSetupIntent(eventId);
            if (!clientSecret) {
                showCardError('Could not start card setup. Please try again.');
                return;
            }

            const { setupIntent, error } = await stripeInstance.confirmCardSetup(clientSecret, {
                payment_method: { card: cardElement },
            });

            if (error) {
                showCardError(error.message || 'Card could not be verified.');
                return;
            }

            const paymentMethodId = setupIntent?.payment_method ?? setupIntent?.paymentMethod;
            if (!paymentMethodId) {
                showCardError('Could not save card. Please try again.');
                return;
            }

            await saveEventPaymentMethod(eventId, typeof paymentMethodId === 'string' ? paymentMethodId : paymentMethodId.id);
            await confirmAfterCardSaved();
        } catch (err) {
            const msg = err.response?.data?.message || 'Could not save card or process deposit. Please try again.';
            showCardError(msg);
        } finally {
            if (loadingWrapper) {
                loadingWrapper.classList.add('hidden');
            }
            submitBtn.classList.remove('disabled');
        }
    });
}
